// Payment Breakdown screen with SUBTOTAL, TIPS, TAXES, and TOTAL
// UI PRESERVED - Only logic improved for Stripe integration

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ArrowRight, Lock, Check } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import PrimaryLogo from '../../../assets/fonts/Primary logo.png';
import { db } from '../../../config/firebase/config';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { StripePaymentService } from '../../../services/stripePaymentService';

const ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '13px',
      color: '#1f2937',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      '::placeholder': {
        color: '#9ca3af',
      },
    },
    invalid: {
      color: '#ef4444',
    },
  },
};

const styles = `
  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  .animate-slide-up {
    animation: slideUp 0.3s ease-out;
  }
  
  .animate-fade-in {
    animation: fadeIn 0.2s ease-out;
  }
`;

const PaymentBreakdownScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subtotal, tipPercentage, tipAmount, mesaId, restaurantId, waiterName, staffId, moneda = 'DOP' } = location.state || {};
  
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('apple-pay');
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [processor, setProcessor] = useState<string | null>(null);
  const [restaurantName, setRestaurantName] = useState<string>('');
  const [showCardForm, setShowCardForm] = useState(false);
  const [loadingProcessor, setLoadingProcessor] = useState(true);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    const fetchRestaurantProcessor = async () => {
      if (!mesaId) {
        console.error('No mesaId provided');
        setLoadingProcessor(false);
        return;
      }

      const resolvedRestaurantId = restaurantId;
      if (!resolvedRestaurantId) {
        console.warn('[LEGACY_ACCESS] PaymentBreakdown: restaurantId missing in state; mesa must be under restaurants/{restaurantId}/mesas.');
        setLoadingProcessor(false);
        return;
      }

      try {
        console.log('🔍 Fetching processor for mesaId:', mesaId, 'restaurantId:', resolvedRestaurantId);

        const mesaRef = doc(db, 'restaurants', resolvedRestaurantId, 'mesas', mesaId);
        const mesaDoc = await getDoc(mesaRef);
        if (!mesaDoc.exists()) {
          console.error('Mesa not found:', mesaId);
          setLoadingProcessor(false);
          return;
        }

        const mesaData = mesaDoc.data();

        // Get restaurant document
        const restaurantDoc = await getDoc(doc(db, 'restaurants', resolvedRestaurantId));
        if (!restaurantDoc.exists()) {
          console.error('Restaurant not found:', restaurantId);
          setLoadingProcessor(false);
          return;
        }

        const restaurantData = restaurantDoc.data();
        const detectedProcessor = restaurantData.processor || 'azul';
        
        console.log('✅ Processor detected:', detectedProcessor);
        console.log('📦 Restaurant config:', {
          processor: restaurantData.processor,
          hasAzulConfig: !!restaurantData.azulConfig,
          hasCardNetConfig: !!restaurantData.cardnetConfig,
          hasStripeConfig: !!restaurantData.stripeConfig
        });
        
        setProcessor(detectedProcessor);
        setRestaurantName(restaurantData.name || 'Restaurante');
        
        // If Stripe, load their Stripe instance with THEIR public key
        console.log('🔍 detectedProcessor:', detectedProcessor);
        console.log('🔍 stripeConfig:', restaurantData.stripeConfig);

        if (
          detectedProcessor === 'stripe' &&
          restaurantData.stripeConfig?.publicKey
        ) {
          console.log(
            '🔑 Stripe public key:',
            restaurantData.stripeConfig.publicKey
          );

          const stripe = loadStripe(
            restaurantData.stripeConfig.publicKey
          );
          setStripePromise(stripe);
        }
        
      } catch (error) {
        console.error('Error fetching processor:', error);
        // Do NOT default to 'azul' — wrong processor sends user to wrong checkout (e.g. Azul when restaurant uses Stripe)
        setProcessor(null);
      } finally {
        setLoadingProcessor(false);
      }
    };
    
    fetchRestaurantProcessor();
  }, [mesaId]);

  // Format currency helper with thousand separators
  const formatCurrency = (amount: number, moneda: string = 'DOP') => {
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
    return `$${formatted} ${moneda}`
  }

  // Incoming "subtotal" from tip-selection is already tax-inclusive. Show Subtotal (pre-tax) + Impuestos (ITBIS):
  const taxRate = 0.18; // 18% tax (ITBIS in DR)
  const amountWithTax = Number(subtotal) || 0;
  const preTaxSubtotal = Math.round((amountWithTax / (1 + taxRate)) * 100) / 100;
  const itbisTax = Math.round((preTaxSubtotal * taxRate) * 100) / 100;
  const total = amountWithTax + (Number(tipAmount) || 0);

  const paymentMethods = [
    { id: 'apple-pay', name: 'Apple Pay', icon: '🍎' },
    { id: 'google-pay', name: 'Google Pay', icon: 'G' },
    { id: 'card', name: 'Tarjeta de Crédito/Débito', icon: '💳' }
  ];

  const StripeCardForm: React.FC<{ onClose: () => void; total: number; moneda: string }> = ({ onClose, total, moneda }) => {
    const stripe = useStripe();
    const elements = useElements();
    const cardSubmitLock = useRef(false);
    const [customerName, setCustomerName] = useState('');
    const [cardProcessing, setCardProcessing] = useState(false);
    const [cardError, setCardError] = useState<string | null>(null);

    // Format currency helper with thousand separators
    const formatCurrency = (amount: number, currency: string = 'DOP') => {
      const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount)
      return `$${formatted} ${currency}`
    }

    const handleCardSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!stripe || !elements) return;
      if (cardSubmitLock.current) return;
      cardSubmitLock.current = true;

      setCardProcessing(true);
      setCardError(null);

      try {
        const cardNumber = elements.getElement(CardNumberElement);
        if (!cardNumber) throw new Error('Card element not found');

        // Phase 1: ask backend to create a Stripe PaymentIntent for this mesa/amount.
        const intentResult = await StripePaymentService.processPurchase({
          mesaId: String(mesaId),
          subtotal: Number(subtotal),
          tipAmount: Number(tipAmount),
          tipPercentage: Number(tipPercentage),
          paymentMethod: 'stripe-card',
        });

        if (!intentResult.success || !intentResult.clientSecret) {
          throw new Error(intentResult.message || 'No se pudo crear el intento de pago');
        }

        // Phase 2: confirm the PaymentIntent client-side with Stripe.js.
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          intentResult.clientSecret,
          {
            payment_method: {
              card: cardNumber,
              billing_details: { name: customerName || undefined },
            },
          }
        );

        if (confirmError || !paymentIntent) {
          throw new Error(confirmError?.message || 'Error al confirmar el pago');
        }

        // Phase 3: notify backend so it can record the transaction and update the mesa.
        const finalizeResult = await StripePaymentService.processPurchase({
          mesaId: String(mesaId),
          subtotal: Number(subtotal),
          tipAmount: Number(tipAmount),
          tipPercentage: Number(tipPercentage),
          paymentMethod: 'stripe-card',
          stripePaymentIntentId: paymentIntent.id,
        });

        if (finalizeResult.approved) {
          navigate('/payment-success-options', {
            state: {
              transactionId: finalizeResult.transactionId,
              authorizationCode: finalizeResult.authorizationCode,
              amount: total,
              mesaId: String(mesaId),
              restaurantId: restaurantId ?? undefined,
              restaurantName: restaurantName,
              serverName: waiterName,
              staffId: staffId,
            },
            replace: true,
          });
        } else {
          setCardError(finalizeResult.message || 'Pago declinado');
          setCardProcessing(false);
          cardSubmitLock.current = false;
        }
      } catch (err: any) {
        setCardError(err.message || 'Error al procesar el pago');
        setCardProcessing(false);
        cardSubmitLock.current = false;
      }
    };

    return (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end justify-center animate-fade-in">
        <div className="bg-[#F2F2F7] w-full h-[95vh] sm:h-auto sm:max-w-md sm:rounded-t-3xl rounded-t-3xl overflow-hidden flex flex-col animate-slide-up">
          {/* Header */}
          <header className="sticky top-0 z-50 backdrop-blur-xl bg-gray-50/80 border-b border-gray-200/50">
            <div className="max-w-md mx-auto px-4 h-12 flex items-center justify-between">
              <button 
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/50 active:scale-95 transition-all text-gray-900"
              >
                <ChevronLeft size={22} />
              </button>
              
              <h1 className="text-sm font-semibold text-gray-900 tracking-tight absolute left-1/2 -translate-x-1/2">
                Agregar Tarjeta
              </h1>

              <div />
            </div>
          </header>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-md mx-auto px-4 py-3 space-y-3">
              {/* Customer Name */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100/50 p-3">
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Nombre del cliente
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-50/70 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:bg-white transition-all border border-transparent focus:border-gray-200"
                  placeholder="Nombre completo"
                />
              </div>

              {/* Card Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100/50 overflow-hidden">
                <div className="px-3 py-2 bg-white border-b border-gray-100/50">
                  <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    Información de la tarjeta
                  </label>
                </div>
                
                <div className="p-3 space-y-3">
                  <div className="bg-gray-50/70 rounded-xl px-3 py-2.5 border border-gray-200/50 focus-within:bg-white focus-within:border-gray-300 transition-all">
                    <CardNumberElement options={ELEMENT_OPTIONS} />
                  </div>

                  <div className="grid grid-cols-2 gap-2.5">
                    <div className="bg-gray-50/70 rounded-xl px-3 py-2.5 border border-gray-200/50 focus-within:bg-white focus-within:border-gray-300 transition-all">
                      <CardExpiryElement options={ELEMENT_OPTIONS} />
                    </div>
                    <div className="bg-gray-50/70 rounded-xl px-3 py-2.5 border border-gray-200/50 focus-within:bg-white focus-within:border-gray-300 transition-all">
                      <CardCvcElement options={ELEMENT_OPTIONS} />
                    </div>
                  </div>
                </div>
              </div>

              {cardError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-[13px] text-red-800 font-medium">{cardError}</p>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Action Bar */}
          <div className="sticky bottom-0 left-0 right-0 p-3 pb-4 z-40 bg-[#F2F2F7] pt-2 border-t border-gray-200/50">
            <div className="max-w-md mx-auto">
              <button
                type="button"
                onClick={handleCardSubmit}
                disabled={!stripe || cardProcessing}
                className={`
                  w-full relative h-14 rounded-xl font-semibold shadow-xl shadow-gray-900/15
                  flex items-center justify-between px-4 transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)]
                  overflow-hidden group
                  ${!cardProcessing && stripe
                    ? 'bg-[#1C1C1E] text-white hover:scale-[1.01] active:scale-[0.98]' 
                    : 'bg-[#E5E5EA] text-gray-400 cursor-not-allowed shadow-none'}
                `}
              >
                {cardProcessing ? (
                  <div className="w-full flex justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2.5">
                      <div className="bg-white/10 p-1 rounded-lg">
                        <Lock size={14} className="text-white" />
                      </div>
                      <span className="text-[13px] font-medium tracking-wide">
                        {stripe ? 'Pagar' : 'Cargando...'}
                      </span>
                    </div>
                    {stripe && (
                      <div className="flex items-center gap-2.5">
                        <span className="text-[15px] font-bold tracking-tight">
                          {formatCurrency(total, moneda)}
                        </span>
                        <ArrowRight size={16} className="opacity-60 group-hover:translate-x-1 transition-transform" />
                      </div>
                    )}
                  </>
                )}
              </button>
              
              <p className="text-center text-[10px] text-gray-400 mt-2">
                Al continuar, acepto los Términos y la Política de Privacidad
              </p>

              {/* Powered by Stripe */}
              <div className="pt-3 border-t border-gray-200/50 mt-3">
                <div className="flex items-center justify-center gap-2">
                  <span className="text-[10px] text-gray-400">Procesado por</span>
                  <svg className="h-4" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#635bff" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.9 0 1.85 6.29.97 6.29 5.88z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handlePay = () => {
    if (loadingProcessor) {
      return;
    }

    if (!processor) {
      alert('Error: No se pudo detectar el procesador de pagos');
      return;
    }

    if (selectedPaymentMethod !== 'card') {
      alert('Apple Pay y Google Pay estarán disponibles pronto. Por favor usa tarjeta de crédito.');
      return;
    }

    // Route based on processor
    if (processor === 'stripe') {
      setShowCardForm(true);
      return;
    }

    if (processor === 'cardnet') {
      navigate('/cardnet-checkout', {
        state: { mesaId, restaurantId, subtotal, tipPercentage, tipAmount, waiterName, staffId }
      });
      return;
    }

    // Default to Azul
      navigate('/azul-checkout', {
        state: { mesaId, restaurantId, subtotal, tipPercentage, tipAmount, waiterName, staffId }
      });
  };

  if (loadingProcessor) {
    return (
      <div className="min-h-screen bg-[#F2F2F7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-[13px]">Cargando información de pago...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2F7] w-full overflow-x-hidden flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300">
      <style>{styles}</style>
      
      {/* Header — same large style as Pagar cuenta completa */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-gray-50/80 border-b border-gray-200/50">
        <div className="max-w-lg mx-auto px-4 flex items-center gap-3 py-5 min-h-[4.5rem]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center justify-center w-10 h-10 -ml-2 rounded-full text-gray-600 hover:bg-gray-200/80 hover:text-gray-900 active:scale-95 transition-all"
            aria-label="Volver"
          >
            <ChevronLeft size={24} strokeWidth={2} />
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight flex-1">
            Métodos de Pago
          </h1>
        </div>
      </header>

      {/* Content — button scrolls; powered by in footer at bottom */}
      <main className="max-w-lg mx-auto px-4 py-3 space-y-3 flex-1 w-full">
        
        {/* Breakdown Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100/50 p-3 relative overflow-hidden">
          <div className="relative z-10 space-y-3.5">
            <div className="flex justify-between items-center text-[13px] py-0.5">
              <span className="text-gray-400">Subtotal</span>
              <span className="text-gray-500 font-medium">{formatCurrency(preTaxSubtotal, moneda)}</span>
            </div>

            <div className="flex justify-between items-center text-[13px] py-0.5">
              <span className="text-gray-400">Impuestos (ITBIS)</span>
              <span className="text-gray-500 font-medium">{formatCurrency(itbisTax, moneda)}</span>
            </div>

            {tipAmount > 0 && (
              <div className="flex justify-between items-center text-[13px] py-0.5">
                <span className="text-gray-400">Propina ({tipPercentage}%)</span>
                <span className="text-gray-500 font-medium">{formatCurrency(tipAmount, moneda)}</span>
              </div>
            )}

            <div className="flex justify-between items-center text-[15px] pt-3 border-t border-gray-100 mt-1">
              <span className="text-gray-400 font-medium">Total</span>
              <span className="text-gray-900 font-semibold">{formatCurrency(total, moneda)}</span>
            </div>
          </div>
        </div>

        {/* Payment Methods Section */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-100/50 overflow-hidden transition-all duration-300">
          <div className="px-3 py-2 bg-white border-b border-gray-100/50">
            <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Pagar con</h2>
          </div>
          
          <div className="divide-y divide-gray-100/50">
            {/* Apple Pay */}
            <div 
              onClick={() => setSelectedPaymentMethod('apple-pay')}
              className={`group flex items-center justify-between px-3 py-2.5 transition-all duration-200 cursor-pointer relative overflow-hidden active:scale-[0.99] ${
                selectedPaymentMethod === 'apple-pay' ? 'bg-blue-50/50' : 'bg-transparent'
              }`}
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="w-5 opacity-100 shrink-0">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    selectedPaymentMethod === 'apple-pay'
                      ? 'bg-gray-900 border-gray-900 scale-100' 
                      : 'border-gray-300 bg-transparent scale-90'
                  }`}>
                    {selectedPaymentMethod === 'apple-pay' && <Check size={10} className="text-white" strokeWidth={3} />}
                  </div>
                </div>
                <span className={`text-[13px] font-medium leading-tight transition-colors ${
                  selectedPaymentMethod === 'apple-pay' ? 'text-gray-900' : 'text-gray-700'
                }`}>
                  Apple Pay
                </span>
              </div>
              <div className="flex items-center">
                <img 
                  src="/images/payment-logos/Apple_Pay_logo.svg.png" 
                  alt="Apple Pay" 
                  className="h-4 w-auto"
                />
              </div>
            </div>

            {/* Google Pay */}
            <div 
              onClick={() => setSelectedPaymentMethod('google-pay')}
              className={`group flex items-center justify-between px-3 py-2.5 transition-all duration-200 cursor-pointer relative overflow-hidden active:scale-[0.99] ${
                selectedPaymentMethod === 'google-pay' ? 'bg-blue-50/50' : 'bg-transparent'
              }`}
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="w-5 opacity-100 shrink-0">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    selectedPaymentMethod === 'google-pay'
                      ? 'bg-gray-900 border-gray-900 scale-100' 
                      : 'border-gray-300 bg-transparent scale-90'
                  }`}>
                    {selectedPaymentMethod === 'google-pay' && <Check size={10} className="text-white" strokeWidth={3} />}
                  </div>
                </div>
                <span className={`text-[13px] font-medium leading-tight transition-colors ${
                  selectedPaymentMethod === 'google-pay' ? 'text-gray-900' : 'text-gray-700'
                }`}>
                  Google Pay
                </span>
              </div>
              <div className="flex items-center">
                <img 
                  src="/images/payment-logos/Google_Pay_Logo.svg.png" 
                  alt="Google Pay" 
                  className="h-5 w-auto"
                />
              </div>
            </div>

            {/* Credit/Debit Card */}
            <div 
              onClick={() => setSelectedPaymentMethod('card')}
              className={`group flex items-center justify-between px-3 py-2.5 transition-all duration-200 cursor-pointer relative overflow-hidden active:scale-[0.99] ${
                selectedPaymentMethod === 'card' ? 'bg-blue-50/50' : 'bg-transparent'
              }`}
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="w-5 opacity-100 shrink-0">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    selectedPaymentMethod === 'card'
                      ? 'bg-gray-900 border-gray-900 scale-100' 
                      : 'border-gray-300 bg-transparent scale-90'
                  }`}>
                    {selectedPaymentMethod === 'card' && <Check size={10} className="text-white" strokeWidth={3} />}
                  </div>
                </div>
                <span className={`text-[13px] font-medium leading-tight transition-colors ${
                  selectedPaymentMethod === 'card' ? 'text-gray-900' : 'text-gray-700'
                }`}>
                  Tarjeta de Crédito/Débito
                </span>
              </div>
              <div className="flex items-center gap-1">
                <img 
                  src="/images/payment-logos/visa-logo-png-extra-large-size.png" 
                  alt="Visa" 
                  className="h-3 w-auto"
                />
                <img 
                  src="/images/payment-logos/Mastercard-logo.svg.png" 
                  alt="Mastercard" 
                  className="h-4 w-auto"
                />
              </div>
            </div>
          </div>
          
          <div className="h-3 bg-gray-50/30" />
        </section>

        {/* Pay button + legal; powered by in footer below */}
        <div className="pt-2 space-y-2">
          {paymentError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-2">
              <p className="text-red-800 text-[11px] font-medium">{paymentError}</p>
              <button
                onClick={() => setPaymentError(null)}
                className="text-red-600 text-[10px] underline mt-1"
              >
                Intentar de nuevo
              </button>
            </div>
          )}

          <button
            onClick={handlePay}
            disabled={processing || loadingProcessor}
            className={`
              w-full relative h-14 rounded-xl font-semibold shadow-xl shadow-gray-900/15
              flex items-center justify-between px-4 transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)]
              overflow-hidden group
              ${!processing && !loadingProcessor
                ? 'bg-[#1C1C1E] text-white hover:scale-[1.01] active:scale-[0.98]' 
                : 'bg-[#E5E5EA] text-gray-400 cursor-not-allowed shadow-none'}
            `}
          >
            <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] ${processing || loadingProcessor ? 'hidden' : ''}`} />
            {processing ? (
              <div className="w-full flex justify-center">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              </div>
            ) : loadingProcessor ? (
              <span className="text-[13px] font-medium">Cargando...</span>
            ) : (
              <>
                <div className="flex items-center gap-2.5">
                  <div className="bg-white/10 p-1 rounded-lg">
                    <Lock size={14} className="text-white" />
                  </div>
                  <span className="text-[13px] font-medium tracking-wide">Pagar</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-[15px] font-bold tracking-tight">
                    {formatCurrency(total, moneda)}
                  </span>
                  <ArrowRight size={16} className="opacity-60 group-hover:translate-x-1 transition-transform" />
                </div>
              </>
            )}
          </button>

          <p className="text-center text-[10px] text-gray-400 mt-2">
            Al continuar, acepto los Términos y la Política de Privacidad
          </p>
        </div>
      </main>

      <footer className="pt-4 pb-8 flex items-center justify-center gap-2 bg-[#F2F2F7]">
        <span className="text-[11px] text-[#8E8E93]">powered by</span>
        <img src={PrimaryLogo} alt="YAP" className="h-10 w-auto object-contain" />
      </footer>
      
      {/* Global CSS for shimmer */}
      <style>{`
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
      `}</style>

      {/* Stripe Card Form Modal - Uses restaurant's Stripe instance */}
      {showCardForm && processor === 'stripe' && stripePromise && (
        <Elements stripe={stripePromise}>
          <StripeCardForm onClose={() => setShowCardForm(false)} total={total} moneda={moneda} />
        </Elements>
      )}
    </div>
  );
};

export default PaymentBreakdownScreen;